import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { isMobile, getAnimationConfig, prefersReducedMotion } from '../utils/mobileOptimization';

const FloatingWhatsApp = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const whatsappNumber = "7982426318";
  const whatsappURL = `https://wa.me/91${whatsappNumber}`;
  const defaultMessage = "Hello! I'm interested in your AI & IT solutions. Can we discuss?";

  useEffect(() => {
    // Show the button after a short delay
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(defaultMessage);
    window.open(`${whatsappURL}?text=${encodedMessage}`, '_blank');
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.2
          }}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50"
        >
          {/* Floating WhatsApp Button */}
          <motion.button
            onClick={handleWhatsAppClick}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            whileHover={!isMobile() ? {
              scale: 1.1,
              boxShadow: '0 20px 40px rgba(37, 211, 102, 0.4)'
            } : {}}
            whileTap={{ scale: 0.95 }}
            animate={!prefersReducedMotion() ? {
              y: [0, isMobile() ? -4 : -8, 0],
              boxShadow: [
                '0 10px 25px rgba(37, 211, 102, 0.3)',
                '0 15px 35px rgba(37, 211, 102, 0.4)',
                '0 10px 25px rgba(37, 211, 102, 0.3)'
              ]
            } : {}}
            transition={!prefersReducedMotion() ? {
              y: {
                duration: isMobile() ? 3 : 2,
                repeat: Infinity,
                ease: "easeInOut"
              },
              boxShadow: {
                duration: isMobile() ? 3 : 2,
                repeat: Infinity,
                ease: "easeInOut"
              }
            } : {}}
            className="relative w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 group overflow-hidden border-2 border-green-400/50 hover:border-green-400/80 active:scale-95 touch-manipulation"
            aria-label="Contact us on WhatsApp"
          >
            {/* Background Glow Effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-green-600/20 rounded-full opacity-0 group-hover:opacity-100"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0, 0.3, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* WhatsApp Logo */}
            <svg
              className="w-7 h-7 sm:w-9 sm:h-9 text-white relative z-10 group-hover:scale-110 transition-transform duration-300"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>


            {/* Ripple Effect - Reduced on mobile */}
            {!prefersReducedMotion() && (
              <motion.div
                className="absolute inset-0 border-2 border-green-400/60 rounded-full"
                animate={{
                  scale: [1, isMobile() ? 1.3 : 1.5, isMobile() ? 1.6 : 2],
                  opacity: [0.8, 0.3, 0]
                }}
                transition={{
                  duration: isMobile() ? 2 : 1.5,
                  repeat: Infinity,
                  ease: "easeOut"
                }}
              />
            )}
          </motion.button>

          {/* Tooltip */}
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, x: 10, scale: 0.8 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 10, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="absolute right-16 sm:right-20 top-1/2 transform -translate-y-1/2 whitespace-nowrap hidden sm:block"
              >
                <div className="bg-gradient-to-r from-gray-900 to-black px-3 sm:px-4 py-2 rounded-xl border border-green-400/30 shadow-xl backdrop-blur-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-xs sm:text-sm font-light tracking-wide">
                      Chat with us on WhatsApp
                    </span>
                  </div>

                  {/* Tooltip Arrow */}
                  <div className="absolute right-0 top-1/2 transform translate-x-full -translate-y-1/2">
                    <div className="w-0 h-0 border-l-8 border-l-gray-900 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pulse Ring Animation - Reduced on mobile */}
          {!prefersReducedMotion() && !isMobile() && (
            <motion.div
              className="absolute inset-0 border-2 border-green-400/30 rounded-full pointer-events-none"
              animate={{
                scale: [1, 2, 2.5],
                opacity: [0.5, 0.2, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeOut",
                delay: 0.5
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingWhatsApp;